import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import dayjs from "dayjs";
import { getSmsSegments, isGsmText } from "../../constants/text";

export type SmsChatPreviewProps = {
  content?: string | null;
  image?: string | null;
  contactName?: string;
  contactPhone?: string;
  sendDate?: string | Date | null;
};

const escapeToHtml = (text: string): string =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br />");

const SmsChatPreview: React.FC<SmsChatPreviewProps> = ({
  content,
  image,
  contactName = "Member",
  contactPhone,
  sendDate,
}) => {
  // Same -7h display convention used everywhere else in the app (see
  // DetailCampaign.tsx / CampaignMemberLogTable.tsx / HistoryCampaignCardContent.tsx).
  const time = useMemo(
    () => (sendDate ? dayjs(sendDate).add(-7, "hour") : dayjs()),
    [sendDate],
  );

  const html = useMemo(
    () => (content ? DOMPurify.sanitize(escapeToHtml(content)) : ""),
    [content],
  );

  const segmentLabel = useMemo(() => {
    if (!content) return null;
    const segments = getSmsSegments(content, !isGsmText(content));
    return `${segments} SMS segment${segments > 1 ? "s" : ""} · ${content.length} characters`;
  }, [content]);

  const initial = contactName.trim().charAt(0).toUpperCase() || "M";

  return (
    <div>
      <div className="sms-chat-shell">
        <div className="sms-chat-status-bar">
          <span>{time.format("HH:mm")}</span>
          <span className="sms-chat-status-icons">
            <span>••••</span>
            <span>📶</span>
            <span>🔋</span>
          </span>
        </div>

        <div className="sms-chat-header">
          <span className="sms-chat-back">‹</span>
          <div className="sms-chat-avatar">{initial}</div>
          <div className="sms-chat-header-name">
            {contactPhone || contactName}
          </div>
          <div className="sms-chat-header-label">Text Message</div>
        </div>

        <div className="sms-chat-body">
          {html && (
            <div className="sms-chat-date-label">
              {time.format("dddd h:mm A")}
            </div>
          )}

          {html ? (
            <div className="sms-chat-row">
              <div className="sms-chat-bubble">
                {image && (
                  <img
                    className="sms-chat-media-img"
                    src={image}
                    alt="Message attachment"
                  />
                )}
                <div
                  className="sms-chat-rich-text"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </div>
            </div>
          ) : (
            <div className="sms-chat-empty">No message content available.</div>
          )}

          {html && <div className="sms-chat-delivered">Delivered</div>}
        </div>

        <div className="sms-chat-input-bar">
          <span className="sms-chat-camera">📷</span>
          <div className="sms-chat-input-pill">Text Message</div>
          <div className="sms-chat-send-btn">↑</div>
        </div>
      </div>

      {segmentLabel && <p className="sms-chat-segment-note">{segmentLabel}</p>}
    </div>
  );
};

export default SmsChatPreview;
